<template>
  <div>
    <div class="row">
      <div class="col-12 mt-3">
        <Card body-classes="p-3" card-classes="shadow-sm">
          <template #header>
            <div class="d-flex flex-row align-items-center justify-content-between">
              <h5 class="mb-0 text-primary">
                <vue-feather type="credit-card" class="me-2"></vue-feather>
                Revenue by Payment Method - {{ store.name }}
              </h5>
              <div class="d-flex align-items-center gap-3">
                <div class="d-flex align-items-center">
                  <label for="fromDate" class="me-2 text-muted">From:</label>
                  <input type="date" id="fromDate" class="form-control form-control-sm" v-model="fromDate">
                </div>
                <div class="d-flex align-items-center">
                  <label for="toDate" class="me-2 text-muted">To:</label>
                  <input type="date" id="toDate" class="form-control form-control-sm" v-model="toDate">
                </div>
                <button class="btn btn-primary btn-sm" @click="getReport">
                  Generate Report
                </button>
              </div>
            </div>
          </template>
          <div>
            <div v-if="loading" class="text-center py-5">
              <Spinner spinner-class="mb-1" />
              <p class="text-muted mt-2">Loading revenue by payment method...</p>
            </div>
            <div v-else-if="reportData">
              <div class="row mb-4">
                <div class="col-md-6">
                  <div class="card shadow-sm">
                    <div class="card-body text-center">
                      <vue-feather type="dollar-sign" size="24" class="text-success mb-2"></vue-feather>
                      <h6 class="text-muted mb-1">Total Revenue</h6>
                      <h4 class="mb-0 text-success">{{ formatNumber(reportData.summary?.total_revenue || 0) }}</h4>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="card shadow-sm">
                    <div class="card-body text-center">
                      <vue-feather type="hash" size="24" class="text-primary mb-2"></vue-feather>
                      <h6 class="text-muted mb-1">Total Transactions</h6>
                      <h4 class="mb-0 text-primary">{{ reportData.summary?.total_transactions || 0 }}</h4>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row mb-4">
                <div class="col-md-6">
                  <div class="card shadow-sm">
                    <div class="card-body">
                      <h6 class="mb-3">Revenue Distribution</h6>
                      <Pie :data="chartData" :options="chartOptions" />
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="card shadow-sm">
                    <div class="card-body">
                      <h6 class="mb-3">Payment Method Details</h6>
                      <div class="table-responsive">
                        <table class="table table-sm">
                          <thead>
                            <tr>
                              <th>Payment Method</th>
                              <th>Transactions</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(item, index) in reportData.details" :key="index">
                              <td><strong>{{ item.payment_method }}</strong></td>
                              <td>{{ item.transaction_count }}</td>
                              <td>{{ formatNumber(item.total_amount) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="alert alert-info">
              <vue-feather type="info" size="18" class="me-2"></vue-feather>
              No data available for the selected period.
            </div>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>
import {decrypt} from "@/utils/crypto";
import analyticsService from "@/api/analyticsService";
import formatNumber from "@/utils/formatNumber";
import { Pie } from 'vue-chartjs';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

export default {
  name: "RevenueByPaymentMethod",
  components: { Pie },

  data(){
    return {
      profile: {},
      store: {},
      loading: false,
      fromDate: '',
      toDate: '',
      reportData: null
    }
  },

  methods: {
    formatNumber,
    async getReport() {
      if (!this.fromDate || !this.toDate) {
        return;
      }

      this.loading = true;
      try {
        const params = {
          store_id: this.store.id,
          from: this.fromDate,
          to: this.toDate
        };

        const response = await analyticsService.getRevenueByPaymentMethod(params);
        this.reportData = response || null;
      } catch(e) {
        console.error(e);
        this.reportData = null;
      } finally {
        this.loading = false;
      }
    },
    setDefaultDates() {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth();
      
      const firstDay = new Date(year, month, 1);
      const fromYear = firstDay.getFullYear();
      const fromMonth = String(firstDay.getMonth() + 1).padStart(2, '0');
      const fromDay = String(firstDay.getDate()).padStart(2, '0');
      
      const toYear = today.getFullYear();
      const toMonth = String(today.getMonth() + 1).padStart(2, '0');
      const toDay = String(today.getDate()).padStart(2, '0');

      this.fromDate = `${fromYear}-${fromMonth}-${fromDay}`;
      this.toDate = `${toYear}-${toMonth}-${toDay}`;
    }
  },

  computed: {
    chartData() {
      if (!this.reportData || !this.reportData.chart) {
        return { labels: [], datasets: [] };
      }

      return {
        labels: this.reportData.chart.labels || [],
        datasets: [
          {
            data: this.reportData.chart.datasets[0]?.data || [],
            backgroundColor: [
              '#28a745',
              '#007bff',
              '#ffc107',
              '#dc3545',
              '#17a2b8',
              '#6c757d'
            ]
          }
        ]
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
          }
        }
      };
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')));
    this.setDefaultDates();
    await this.getReport();
  }
}
</script>

<style scoped>
.table th {
  background-color: #f8f9fa;
  font-weight: 600;
}
canvas {
  height: 300px !important;
}
</style>
